import type React from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../utils/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!email.trim() || !email.includes("@")) {
      return setError("Enter a valid email address.");
    }
    setLoading(true);
    try {
      // Enumeration-safe: the server returns 200 whether or not the email
      // exists, and the confirmation copy below stays conditional to match.
      await api("/api/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError("Too many requests. Wait a few minutes, then try again.");
      } else {
        setError("Couldn't send the reset link. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div style={styles.page}>
        <div style={styles.card} aria-live="polite">
          <div style={styles.brandBadge}>StintSense</div>
          <div style={styles.doneMark}>✓</div>
          <h2 style={styles.title}>Check your inbox</h2>
          <p style={styles.copy}>
            If an account exists for that email, we've sent a reset link. It may
            take a minute to arrive.
          </p>
          <div style={styles.footerNote}>
            <Link to="/login" style={styles.footerLink}>
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandBadge}>StintSense</div>
        <div style={styles.header}>
          <div className="eyebrow" style={{ marginBottom: "0.75rem" }}>
            Account recovery
          </div>
          <h2 style={styles.title}>Reset your password</h2>
          <p style={styles.copy}>
            Enter the email on your account and we'll send you a link to set a
            new password.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.formBody} noValidate>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Email</span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
            />
          </label>

          {error && <div style={styles.errorBanner} role="alert">{error}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading} style={styles.submitButton}>
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>

        <div style={styles.footerNote}>
          Remembered it?{" "}
          <Link to="/login" style={styles.footerLink}>
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.25rem",
  },
  card: {
    width: "min(27rem, 100%)",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--rule)",
    background: "var(--ink-2)",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.025)",
    padding: "2rem 1.75rem",
    display: "flex",
    flexDirection: "column",
  },
  brandBadge: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "0.4rem 0.85rem",
    borderRadius: "999px",
    border: "1px solid var(--live-edge)",
    background: "var(--live-soft)",
    color: "var(--live-strong)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    marginBottom: "1.5rem",
  },
  header: {
    marginBottom: "1.25rem",
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    marginBottom: "0.45rem",
    color: "var(--text-strong)",
  },
  copy: {
    color: "var(--text-secondary)",
    fontSize: "0.98rem",
    lineHeight: 1.7,
  },
  formBody: {
    display: "flex",
    flexDirection: "column",
    gap: "0.95rem",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
  },
  fieldLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  errorBanner: {
    padding: "0.85rem 1rem",
    borderRadius: "var(--r-sm)",
    border: "1px solid rgba(248, 113, 113, 0.28)",
    background: "var(--flag-red-soft)",
    color: "var(--flag-red)",
    fontWeight: 600,
  },
  submitButton: {
    width: "100%",
    marginTop: "0.25rem",
  },
  footerNote: {
    marginTop: "1rem",
    paddingTop: "1rem",
    borderTop: "1px solid var(--rule)",
    color: "var(--text-muted)",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  footerLink: {
    color: "var(--live-strong)",
    fontWeight: 600,
    textDecoration: "none",
  },
  doneMark: {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "999px",
    display: "grid",
    placeItems: "center",
    fontSize: "1.6rem",
    color: "var(--flag-green)",
    border: "1px solid var(--flag-green)",
    background: "var(--flag-green-soft, rgba(74, 222, 128, 0.12))",
    marginBottom: "1rem",
  },
};
